import React, { ReactElement, useEffect, useReducer, useState } from 'react'
import { observer } from 'mobx-react'
import styled from 'styled-components'
import { Container } from '../components/Animations'
import { PrimaryButton } from '../components/Button'
import ConfirmButtonModal from '../components/ConfirmButtonModal'
import Gate from '../components/Gate'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Dots from '../icons/Dots'
import Table from '../components/Table'
import TimeText from '../components/TimeText'
import UserEditModal from '../components/UserEditModal'
import UserInviteModal, { ICreateUser } from '../components/UserInviteModal'
import Radom, { IPermissionRole, Member, TeamInvite } from '../state/Radom'
import Web3 from '../state/User'
import { RADOM_COLORS } from '../util/Constants'
import { usePermissions } from '../util/permissions'
import { errorToast, infoToast, successToast } from '../util/Util'
import {
  SettingsCard,
  SettingsHero,
  SettingsPage,
  SettingsSubtext,
  SettingsTitle,
  StatusPill
} from './Organization/SettingsLayout'

const RolePill = styled.span`
  background: ${RADOM_COLORS.GRAY_MED};
  border-radius: 6px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  white-space: nowrap;
`

const MemberCell = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  min-width: 0;
`

const MemberName = styled.span`
  color: var(--ink, #20242d);
  font-size: 13px;
  font-weight: 450;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MemberSub = styled.span`
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 11px;
  margin-top: 1px;
`

const truncateAddress = (addr: string): string => {
  if (addr.length <= 14) return addr
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`
}

interface UserState {
  userForm: ICreateUser
  userIsEditing: boolean
  createUserModalVisible: boolean
  deleteUserModalVisible: boolean
  userToDelete: any
}

const TeamSettings = observer((): ReactElement => {
  const { has, isLoaded } = usePermissions()
  const [invites, setInvites] = useState([] as TeamInvite[])
  const [teamUsers, setTeamUsers] = useState([] as Member[])
  const [apiRoles, setAPIRoles] = useState([] as IPermissionRole[])
  const [isLoadingAPIRoles, setIsLoadingAPIRoles] = useState(false)
  const [closeDropdownFns, updateCloseDropdownFns] = useReducer(
    (prev: Record<string, () => void>, next: Record<string, () => void>) => ({ ...prev, ...next }),
    {}
  )

  const [userState, updateUserState] = useReducer((oldState: UserState, newState: Partial<UserState>) => {
    return { ...oldState, ...newState }
  }, {
    userForm: { emailAddress: undefined, roles: [] },
    userIsEditing: false,
    createUserModalVisible: false,
    deleteUserModalVisible: false,
    userToDelete: undefined
  })

  const [editUserModalVisible, setEditUserModalVisible] = useState(false)
  const [editUser, setEditUser] = useState<Member>()
  const [editUserRoles, setEditUserRoles] = useState([] as string[])

  const listPermissionRoles = (): void => {
    if (!has('ReadRoles')) return
    setIsLoadingAPIRoles(true)
    Radom.listPermissionRoles()
      .then(r => setAPIRoles(r))
      .finally(() => setIsLoadingAPIRoles(false))
  }

  const listTeamMembers = (): void => {
    Radom.listMembers().then(m => setTeamUsers(m))
  }

  const listTeamInvites = (): void => {
    Radom.listInvites().then(m => setInvites(m))
  }

  useEffect(() => {
    if (!isLoaded) return
    listPermissionRoles()
    listTeamMembers()
    listTeamInvites()
  }, [Web3.isConnected, Radom.isLoggedIn, isLoaded])

  const onDeleteInvite = async (inviteId: string): Promise<void> => {
    Radom.deleteInvite(inviteId)
      .then(() => {
        successToast('Successfully deleted invite')
        listTeamInvites()
      }).catch(err => {
        errorToast(err.response?.data?.error || err.message)
      })
  }

  const onDeleteUser = async (userId: string): Promise<void> => {
    Radom.deleteMember(userId)
      .then(() => {
        successToast('Successfully removed user')
        listTeamMembers()
      }).catch(err => {
        errorToast(err.response?.data?.error || err.message)
      })
  }

  return (
    <Container>
      <SettingsPage>

        <SettingsHero>
          <div>
            <SettingsTitle>Members</SettingsTitle>
            <SettingsSubtext>
              Manage people who have access to this organization.
            </SettingsSubtext>
          </div>
          <Gate permission='WriteTeam'>
            <PrimaryButton
              onClick={() => updateUserState({
                createUserModalVisible: true,
                userForm: { emailAddress: undefined, roles: [] },
                userIsEditing: false
              })}
              style={{ gap: 8, whiteSpace: 'nowrap', minHeight: 36, fontSize: 13, padding: '0 14px' }}>
              <span style={{ fontSize: 14, lineHeight: 1 }}>+</span>
              <span>Invite member</span>
            </PrimaryButton>
          </Gate>
        </SettingsHero>

        <SettingsCard>
          <Table
            mergeHeader
            headers={[
              <span key="user">Member</span>,
              <span key="role">Roles</span>,
              <span key="lastLogin">Last login</span>,
              <span key="menu"></span>
            ]}
            isLoading={isLoadingAPIRoles}
            items={
              teamUsers.map(user => {
                const userRoles = apiRoles.filter(r => r.users.includes(user.userId))
                const displayName = user.emailAddress ??
                  (user.evmAddress ? truncateAddress(user.evmAddress) : user.userId)

                return [
                  <MemberCell key="userAddr">
                    <div style={{ minWidth: 0 }}>
                      <MemberName>{displayName}</MemberName>
                      {user.evmAddress && user.emailAddress && (
                        <MemberSub>{truncateAddress(user.evmAddress)}</MemberSub>
                      )}
                    </div>
                  </MemberCell>,
                  <div key="roles" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    {userRoles.length > 0
                      ? userRoles.map(r => <RolePill key={r.id}>{r.name}</RolePill>)
                      : <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 13 }}>—</span>}
                  </div>,
                  <span key="lastLogin" style={{ color: user.lastLoginAt ? 'var(--ink, #20242d)' : RADOM_COLORS.GRAY_DARKER, fontSize: 13 }}>
                    {user.lastLoginAt ? <TimeText date={new Date(user.lastLoginAt)} /> : '—'}
                  </span>,
                  <div key="menu" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Gate permission='WriteTeam'>
                      <Dropdown
                        key={user.userId}
                        noChevron
                        iconTrigger
                        selectedContent={<Dots width={15} />}
                        dropdownContent={
                          <div style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                            <DropdownItem onClick={() => {
                              setEditUser(user)
                              setEditUserRoles(apiRoles.filter(r => r.users.includes(user.userId)).map(r => r.id))
                              setEditUserModalVisible(true)
                            }}>Edit member</DropdownItem>
                            <ConfirmButtonModal
                              title='Remove member'
                              description='Are you sure you want to remove this member from your organization?'
                              action='Remove member'
                              onConfirm={async () => { await onDeleteUser(user.userId) }}
                              ActionComponent={(props: React.HTMLAttributes<HTMLDivElement>) =>
                                <DropdownItem {...props} style={{ color: RADOM_COLORS.ERROR }}>
                                Remove member
                                </DropdownItem>
                              }
                            />
                          </div>
                        }
                      />
                    </Gate>
                  </div>
                ]
              }).concat(invites.map(invite => {
                const displayName = invite.emailAddress ??
                  (invite.evmAddress ? truncateAddress(invite.evmAddress) : invite.id)
                const inviteRoles = apiRoles.filter(r => invite.roles.includes(r.id))

                return [
                  <MemberCell key="inviteAddr">
                    <div style={{ minWidth: 0 }}>
                      <MemberName style={{ opacity: 0.7 }}>{displayName}</MemberName>
                    </div>
                  </MemberCell>,
                  <div key="inviteRoles" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    {inviteRoles.length > 0
                      ? inviteRoles.map(r => <RolePill key={r.id}>{r.name}</RolePill>)
                      : <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 13 }}>—</span>}
                  </div>,
                  <StatusPill key="inviteStatus" $tone='amber'>Invite pending</StatusPill>,
                  <div key="inviteMenu" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Dropdown
                      key={invite.id}
                      noChevron
                      iconTrigger
                      selectedContent={<Dots width={15} />}
                      onCloseFn={f => updateCloseDropdownFns({ [invite.id]: f })}
                      dropdownContent={
                        <div style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                          <DropdownItem onClick={() => {
                            navigator.clipboard.writeText(invite.inviteUrl)
                            infoToast('Invite link copied to clipboard')
                            closeDropdownFns[invite.id]()
                          }}>Copy invite link</DropdownItem>
                          <Gate permission='WriteTeam'>
                            <ConfirmButtonModal
                              title='Revoke invite'
                              description='Are you sure you want to revoke this invite?'
                              action='Revoke invite'
                              onConfirm={async () => { await onDeleteInvite(invite.id) }}
                              ActionComponent={(props: React.HTMLAttributes<HTMLDivElement>) =>
                                <DropdownItem {...props} style={{ color: RADOM_COLORS.ERROR }}>
                                Revoke invite
                                </DropdownItem>
                              }
                            />
                          </Gate>
                        </div>
                      }
                    />
                  </div>
                ]
              }))
            }
            emptyPlaceholderText='No members yet — invite your first team member'
          />
        </SettingsCard>

        <UserInviteModal
          visible={userState.createUserModalVisible}
          isEditing={userState.userIsEditing}
          userForm={userState.userForm}
          onClose={() => {
            updateUserState({ createUserModalVisible: false })
            listTeamInvites()
          }}
          onRoleAssignment={() => listPermissionRoles()}
          onUserFormUpdate={(u) => updateUserState({ userForm: { ...userState.userForm, ...u } })}
        />

        {editUser && (
          <UserEditModal
            visible={editUserModalVisible}
            currentRoles={editUserRoles}
            user={editUser}
            onClose={() => {
              setEditUserModalVisible(false)
              listPermissionRoles()
            }}
          />
        )}

      </SettingsPage>
    </Container>
  )
})

export default TeamSettings
